import * as React from 'react';
import { useEffect, useState } from 'react';
import { SectionCard, TextInput } from '../../../components/forms';
import { DELIVERABLE_STATUSES, DESIGN_STATUSES, fullName, label, ProductionApi, reason, RowSelect, useInflight } from './parts';

/*
 * Design requests and their deliverables. Each change saves on its own,
 * like v2; a control stays on its new value only once the server agrees.
 */

const YMD = /^\d{4}-\d{2}-\d{2}$/;
/* a DATE column arrives as midnight UTC; read the calendar day, not local time */
const dueDay = (v?: string | null) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '');

/** Date field that saves once a whole date is in, not on every keystroke. */
function DueInput({ value, disabled, onSave }: { value: string; disabled: boolean; onSave: (v: string) => void }) {
    const [local, setLocal] = useState(value);
    useEffect(() => setLocal(value), [value]);
    return (
        <TextInput
            type="date"
            value={local}
            disabled={disabled}
            onChange={(e) => {
                const v = e.target.value;
                setLocal(v);
                /* partial years while typing (0002…) aren't saved */
                if (v === value || (v && (!YMD.test(v) || +v.slice(0, 4) < 1900))) return;
                onSave(v);
            }}
            onBlur={() => setLocal(value)}
        />
    );
}

export default function DesignSection({
    api,
    activationId,
    requests,
    staff,
    refresh,
    toast,
}: {
    api: ProductionApi;
    activationId: number;
    requests: any[];
    staff: any[];
    refresh: () => Promise<void>;
    toast: (text: string) => void;
}) {
    const { busy, run } = useInflight();
    const [error, setError] = useState<string | null>(null);
    /* value shown while its save is in flight */
    const [pending, setPending] = useState<Record<string, string>>({});
    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const clearPending = (key: string) =>
        setPending((p) => {
            const next = { ...p };
            delete next[key];
            return next;
        });

    const create = async () => {
        setError(null);
        const r = await run('create', () => api.createDesignRequest({ activationId }));
        if (r.ok) {
            await refresh();
            toast('Design request added.');
        } else if (!('skipped' in r && r.skipped)) setError(reason(r.error, 'Couldn’t add a design request — try again.'));
    };

    const updateRequest = async (d: any, field: string, value: string, body: Record<string, any>, what: string) => {
        const key = `req-${d.id}-${field}`;
        setError(null);
        setPending((p) => ({ ...p, [key]: value }));
        const r = await run(key, () => api.updateDesignRequest(+d.id, body));
        if (r.ok) await refresh();
        else if (!('skipped' in r && r.skipped)) setError(reason(r.error, `Couldn’t change the ${what} — it’s unchanged.`));
        clearPending(key);
    };

    const updateDeliverable = async (del: any, status: string) => {
        const key = `del-${del.id}`;
        setError(null);
        setPending((p) => ({ ...p, [key]: status }));
        const r = await run(key, () => api.updateDeliverable(+del.id, { status }));
        if (r.ok) await refresh();
        else if (!('skipped' in r && r.skipped)) setError(reason(r.error, `Couldn’t update “${del.label}” — it’s unchanged.`));
        clearPending(key);
    };

    const addDeliverable = async (d: any) => {
        const text = (drafts[d.id] ?? '').trim();
        if (!text) return;
        setError(null);
        const r = await run(`add-${d.id}`, () => api.createDeliverable(+d.id, { label: text }));
        if (r.ok) {
            setDrafts((m) => ({ ...m, [d.id]: '' }));
            await refresh();
        } else if (!('skipped' in r && r.skipped)) {
            setError(reason(r.error, `Couldn’t add “${text}” — try again.`));
        }
    };

    const designers = [...staff].sort((a, b) => fullName(a).localeCompare(fullName(b)));

    return (
        <SectionCard
            title="Design requests"
            note={requests.length ? String(requests.length) : undefined}
            action={
                <button type="button" className="tb-btn sm" disabled={!!busy.create} onClick={create}>
                    {busy.create ? 'Adding…' : 'New request'}
                </button>
            }
        >
            {requests.length === 0 && <p className="quiet-empty">No design requests.</p>}
            {requests.map((d, i) => {
                const st = pending[`req-${d.id}-status`] ?? d.status;
                const due = pending[`req-${d.id}-dueDate`] ?? dueDay(d.dueDate);
                const designer = pending[`req-${d.id}-designer`] ?? (d.assignedDesignerStaffId != null ? String(d.assignedDesignerStaffId) : '');
                const designerKnown = !designer || designers.some((s) => String(s.id) === designer);
                const dels: any[] = d.deliverables ?? [];
                return (
                    <div className="prd-req" key={d.id}>
                        <p className="s-lab prd-req-h">Request {i + 1}</p>
                        <div className="prd-req-f">
                            <label className="field">
                                <span className="field-l">Status</span>
                                <RowSelect
                                    value={st}
                                    options={DESIGN_STATUSES}
                                    disabled={!!busy[`req-${d.id}-status`]}
                                    ariaLabel={`Request ${i + 1} status`}
                                    onChange={(v) => v !== d.status && updateRequest(d, 'status', v, { status: v }, 'status')}
                                />
                            </label>
                            <label className="field">
                                <span className="field-l">Due</span>
                                <DueInput
                                    value={due}
                                    disabled={!!busy[`req-${d.id}-dueDate`]}
                                    onSave={(v) => updateRequest(d, 'dueDate', v, { dueDate: v || null }, 'due date')}
                                />
                            </label>
                            <label className="field">
                                <span className="field-l">Designer</span>
                                <span className="selectwrap prd-rsel">
                                    <select
                                        className="input select"
                                        value={designer}
                                        disabled={!!busy[`req-${d.id}-designer`]}
                                        onChange={(e) =>
                                            updateRequest(
                                                d,
                                                'designer',
                                                e.target.value,
                                                { assignedDesignerStaffId: e.target.value === '' ? null : +e.target.value },
                                                'designer',
                                            )
                                        }
                                    >
                                        <option value="">Unassigned</option>
                                        {!designerKnown && (
                                            <option value={designer}>
                                                {fullName({ firstName: d.designerFirstName, lastName: d.designerLastName })}
                                            </option>
                                        )}
                                        {designers.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {fullName(s)}
                                            </option>
                                        ))}
                                    </select>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </span>
                            </label>
                        </div>
                        {dels.map((del) => (
                            <div className="trow2 prd-row" key={del.id}>
                                <span className="prd-main">
                                    <span className="nm2">{del.label}</span>
                                    {del.type && <span className="sub2">{label(del.type)}</span>}
                                </span>
                                <span className="prd-ctl">
                                    <RowSelect
                                        value={pending[`del-${del.id}`] ?? del.status}
                                        options={DELIVERABLE_STATUSES}
                                        disabled={!!busy[`del-${del.id}`]}
                                        ariaLabel={`${del.label} status`}
                                        onChange={(v) => v !== del.status && updateDeliverable(del, v)}
                                    />
                                </span>
                            </div>
                        ))}
                        <form
                            className="prd-add"
                            onSubmit={(e) => {
                                e.preventDefault();
                                addDeliverable(d);
                            }}
                        >
                            <span className="prd-grow">
                                <TextInput
                                    placeholder="Add a deliverable…"
                                    aria-label={`New deliverable for request ${i + 1}`}
                                    value={drafts[d.id] ?? ''}
                                    disabled={!!busy[`add-${d.id}`]}
                                    onChange={(e) => setDrafts((m) => ({ ...m, [d.id]: e.target.value }))}
                                />
                            </span>
                            <button type="submit" className="tb-btn" disabled={!(drafts[d.id] ?? '').trim() || !!busy[`add-${d.id}`]}>
                                {busy[`add-${d.id}`] ? 'Adding…' : 'Add'}
                            </button>
                        </form>
                    </div>
                );
            })}
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}
        </SectionCard>
    );
}
